// Shared contract for the post-signup "finish your profile" nudge (SILL-398).
//
// The anon chat door records which GOOD-DATA fields the user did NOT provide
// before early-ejecting — the platform fills neutral assumptions for them at
// stitch (CompleteAnon.reconcileChatDoorDraft). The dashboard reads this set
// once on first authenticated load to prompt the user to replace the
// assumptions with their real numbers. Session-scoped (no backend/migration):
// the chat is the only writer and it knows exactly what the user provided.

import type { DashboardSnapshot } from "./dashboard";

export const FINISH_PROFILE_STORAGE_KEY = "sill_finish_profile";

// GOOD-DATA fields (everything outside the KEY tier). Labels are user-facing.
// Keys match the intake extraction field names (services/anon + agent/intake).
export const GOOD_DATA_FIELD_LABELS: Record<string, string> = {
  savings: "Savings for down payment & closing",
  monthly_debts: "Monthly debt payments",
  buyer_type: "What kind of move this is",
  credit_score_range: "Credit score range",
  timeline: "Your buying timeline",
  housing_situation: "Your current housing situation",
  // The chat door never asks for employment; the platform assumes a qualifying
  // 2 years so the readiness score isn't gated by an employment DNQ. Always
  // surfaced here so the user confirms or corrects it.
  employment_months: "Employment history",
};

export const GOOD_DATA_FIELDS = Object.keys(GOOD_DATA_FIELD_LABELS);

/** Record the GOOD-DATA fields the user did not provide (so they were assumed). */
export function recordAssumedFields(provided: Record<string, unknown>): void {
  try {
    const assumed = GOOD_DATA_FIELDS.filter(
      (f) => provided[f] == null || provided[f] === "",
    );
    if (assumed.length > 0) {
      sessionStorage.setItem(
        FINISH_PROFILE_STORAGE_KEY,
        JSON.stringify(assumed),
      );
    } else {
      sessionStorage.removeItem(FINISH_PROFILE_STORAGE_KEY);
    }
  } catch {
    // sessionStorage can throw in private-mode/quota edge cases — non-fatal.
  }
}

/** Read the assumed-field set persisted at handoff (empty when none). */
export function readAssumedFields(): string[] {
  try {
    const raw = sessionStorage.getItem(FINISH_PROFILE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (f): f is string => typeof f === "string" && f in GOOD_DATA_FIELD_LABELS,
    );
  } catch {
    return [];
  }
}

/** Merge server-derived assumptions with the legacy handoff set. Unknown
 * keys are dropped so the finish-profile surface remains fail-closed. */
export function mergeAssumedFields(
  ...sets: ReadonlyArray<readonly string[]>
): string[] {
  return Array.from(
    new Set(sets.flat().filter((field) => field in GOOD_DATA_FIELD_LABELS)),
  );
}

// The subset of the dashboard snapshot needed to decide whether an assumed
// GOOD-DATA field has since been given a real value. Typed as a Pick so the
// reconciler stays decoupled from the full snapshot but type-safe at the call
// site (DashboardPage passes the real DashboardSnapshot).
export type AssumedFieldPresence = Pick<
  DashboardSnapshot,
  | "savings"
  | "monthlyDebts"
  | "buyerType"
  | "creditBucket"
  | "timelineToken"
  | "housingSituation"
  | "employmentMonths"
>;

// Presence predicates per GOOD-DATA field, mirroring the completeness
// `isFilled` checks in dashboard.ts (PROFILE_COMPLETENESS_FIELDS) but read off
// the already-projected snapshot. A field with no predicate is conservatively
// kept (never silently dropped). $0 monthly debts stays "unconfirmed" — the
// neutral assumed default — so the user is still nudged to confirm it.
const ASSUMED_FIELD_PRESENT: Record<
  string,
  (s: AssumedFieldPresence) => boolean
> = {
  savings: (s) => (s.savings ?? 0) > 0,
  monthly_debts: (s) => (s.monthlyDebts ?? 0) > 0,
  buyer_type: (s) => Boolean(s.buyerType?.trim()),
  credit_score_range: (s) => Boolean(s.creditBucket),
  timeline: (s) => Boolean(s.timelineToken),
  housing_situation: (s) => Boolean(s.housingSituation?.trim()),
  employment_months: (s) => s.employmentMonths != null,
};

/**
 * Drop assumed fields the user has since filled. The chat door records what it
 * assumed at intake time (point-in-time), so the raw list goes stale the moment
 * real or verified data lands (doc upload, bank connect, manual edit). Reconcile
 * it against the live snapshot before showing the nudge so a complete profile
 * isn't told to "finish" fields it already has.
 */
export function pruneAssumedFields(
  assumed: string[],
  snapshot: AssumedFieldPresence,
): string[] {
  return assumed.filter((field) => {
    const isPresent = ASSUMED_FIELD_PRESENT[field];
    // Unknown field (no predicate) → keep it; only drop when we can prove
    // the value is present.
    return isPresent ? !isPresent(snapshot) : true;
  });
}

/** Clear the nudge so the dashboard modal shows at most once. */
export function clearAssumedFields(): void {
  try {
    sessionStorage.removeItem(FINISH_PROFILE_STORAGE_KEY);
  } catch {
    // non-fatal
  }
}
